import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import { Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import Button from "components/atoms/Button/Button";
import Input from "components/atoms/Input/Input";
import Radio from "components/atoms/Radio/Radio";
import Dialog from "components/organisms/Dialog/Dialog";
import { createInvoiceAttachmentBucket, updateInvoiceAttachmentBucket } from "api";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";

function AttachmentBucketModal(props: any) {
  const { isOpen, close, isEdit, selectedBucket, refetch } = props || {};
  const intl = useIntl();
  const { permissions } = usePermissions();
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;
  const companyId = JSON.parse(permissions || "{}")?.company_id;
  const viewOnly = Boolean(!adminSettings?.edit_attachment_buckets);
  const methods = useFormContext();
  const { handleSubmit, reset, setValue } = methods;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isEdit && selectedBucket) {
      setValue("is_active", selectedBucket?.is_active ? "Active" : "Inactive");
      setValue("bucket_name", selectedBucket?.bucket_name);
    }
  }, [selectedBucket, isEdit]);

  const statusOptions = [
    {
      value: "Active",
      label: intl.formatMessage({ id: "general.active", defaultMessage: "Active" }),
    },
    {
      value: "Inactive",
      label: intl.formatMessage({ id: "general.inActive", defaultMessage: "Inactive" }),
    },
  ];

  const getDialogTitle = () => {
    if (selectedBucket?.bucket_name) {
      return intl.formatMessage({
        id: "settings.invoice-attachment.invoiceAttachmentBucketDetails",
        defaultMessage: "Invoice attachment bucket details",
      });
    }
    return intl.formatMessage({
      id: "settings.invoice-attachment.newAttachmentBucketDetails",
      defaultMessage: "New attachment bucket details",
    });
  };

  const onSubmit = (data: any) => {
    const newData: any = {};
    if (data?.is_active === "Active") {
      newData.is_active = true;
      newData.bucket_name = data?.bucket_name;
    } else {
      newData.is_active = false;
      newData.bucket_name = data?.bucket_name;
    }
    setIsLoading(true);
    if (isEdit) {
      updateInvoiceAttachmentBucket(selectedBucket?.id, newData)
        .then(() => {
          toast(
            intl.formatMessage({
              id: "settings.invoice-attachment.updated",
              defaultMessage: "Invoice Attachment Bucket updated successfully",
            }),
            { type: "success" },
          );
          reset();
          close();
          refetch();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsLoading(false));
    } else {
      createInvoiceAttachmentBucket({ ...newData, company: companyId })
        .then(() => {
          toast(
            intl.formatMessage({
              id: "settings.invoice-attachment.created",
              defaultMessage: "Invoice Attachment Bucket created successfully",
            }),
            { type: "success" },
          );
          reset();
          close();
          refetch();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <Dialog size="sm" title={getDialogTitle()} show={isOpen} onHide={close}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: 565 }}>
        <Row className="mb-3 mt-3">
          <Col lg={4}>
            <div className="mb-3 font-b1 font-weight-bold required h-100 d-flex align-items-center">
              <FormattedMessage id="general.status" defaultMessage="Status" />
            </div>
          </Col>

          <Col lg={8}>
            {viewOnly ? (
              selectedBucket?.is_active ? (
                <FormattedMessage id="general.active" defaultMessage="Active" />
              ) : (
                <FormattedMessage id="general.inActive" defaultMessage="Inactive" />
              )
            ) : (
              <Radio
                options={statusOptions}
                name="is_active"
                defaultChecked={selectedBucket?.is_active ? "Active" : "Inactive"}
                customOption
                className="border-0 w-50 task-dialog-radio"
                required
              />
            )}
          </Col>
        </Row>

        <Row className="d-flex pt-3 pb-8">
          <Col lg={4} className="d-flex align-items-center ">
            <div className="font-b1 font-weight-bold required">
              <FormattedMessage id="settings.invoice-attachment.bucketTitle" defaultMessage="Attachment bucket title" />
            </div>
          </Col>
          <Col lg={8}>
            {viewOnly ? (
              <span>{selectedBucket?.bucket_name}</span>
            ) : (
              <Input
                type="text"
                required
                name="bucket_name"
                placeholder={intl.formatMessage({
                  id: "general.typeHere",
                  defaultMessage: "Type here...",
                })}
                validations={{
                  required: intl.formatMessage({
                    id: "settings.invoice-attachment.requiredField",
                    defaultMessage: "Field is required",
                  }),
                }}
                className="rounded-input w-100"
              />
            )}
          </Col>
        </Row>

        <Row className="mt-5">
          <Col lg={12} className="d-flex justify-content-between">
            <Button
              variant="outline-primary"
              label={
                isEdit && viewOnly === false
                  ? intl.formatMessage({ id: "settings.address-book.discardChanges", defaultMessage: "Discard changes" })
                  : intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })
              }
              size="lg"
              onClick={close}
            />
            {!isEdit ? (
              <Button
                variant="primary"
                type="submit"
                size="lg"
                label={intl.formatMessage({
                  id: "settings.invoice-attachment.createAttachmentBucket",
                  defaultMessage: "Create attachment bucket",
                })}
                isLoading={isLoading}
              />
            ) : !viewOnly ? (
              <Button
                variant="primary"
                type="submit"
                size="lg"
                label={intl.formatMessage({
                  id: "general.save",
                  defaultMessage: "Save",
                })}
                isLoading={isLoading}
              />
            ) : null}
          </Col>
        </Row>
      </form>
    </Dialog>
  );
}

export default AttachmentBucketModal;
